import axios, { AxiosInstance } from 'axios';
import { includes } from 'lodash';
import { TMetadata } from 'types/metadata';

import { ErrorHandlingManager } from '@classes/errorHandlingManager';
import { messageService } from '@classes/messageService';

import { GlobalConstants } from '../constants/global';
import { RequestResult } from './responseModels/requestResult';
import { CRM_ID_REQUIRED_MESSAGE, isCrmIdMissing } from './validateCrmId';

class CRMAPIBase {
    protected _httpClient: AxiosInstance;
    protected _token?: string;

    constructor(token?: string) {
        this._httpClient = axios.create({
            baseURL: GlobalConstants.BaseUrl,
            timeout: GlobalConstants.RequestTimeout,
        });
        if (token) {
            this._token = token;
            this._httpClient.defaults.headers.common['Authorization'] = 'Bearer ' + this._token;
        }
    }

    /**
     * Отправка GET запроса
     * @param url url запроса
     * @param params Параметры для строки запроса
     * @returns Результат запроса
     */
    protected async get<T>(url: string, params?: any): Promise<RequestResult<T>> {
        return this.request('GET', url, null, params);
    }

    /**
     * Отправка POST запроса
     * @param url url запроса
     * @param data Параметры для тела запроса
     * @param params Параметры для строки запроса
     * @returns Результат запроса
     */
    protected async post<T>(
        url: string,
        data?: any,
        params?: any,
        headers: any = {}
    ): Promise<RequestResult<T>> {
        return this.request('POST', url, data, params, headers);
    }

    /**
     * Отправка PUT запроса
     * @param url url запроса
     * @param data Параметры для тела запроса
     * @param params Параметры для строки запроса
     * @returns Результат запроса
     */
    protected async put<T>(url: string, data?: any, params?: any): Promise<RequestResult<T>> {
        return this.request('PUT', url, data, params);
    }

    /**
     * Отправка DELETE запроса
     * @param url url запроса
     * @param params Параметры для тела запроса
     * @returns Результат запроса
     */
    protected async delete<T>(url: string, params?: any): Promise<RequestResult<T>> {
        return this.request('DELETE', url, null, params);
    }

    /**
     * Отправка Общего запроса
     * @param method Метод запроса
     * @param url url запроса
     * @param data Параметры для тела запроса
     * @param params Параметры для строки запроса
     * @returns Результат запроса
     */
    private async request<T>(
        method: string,
        url: string,
        data?: any,
        params?: any,
        headers: any = {}
    ): Promise<RequestResult<T>> {
        // TODO: временно отключена проверка пустого crm_id
        // if (isCrmIdMissing(data, params)) {
        //     messageService.sendError(CRM_ID_REQUIRED_MESSAGE);
        //     throw new Error(CRM_ID_REQUIRED_MESSAGE);
        // }

        let result = new RequestResult<T>();
        try {
            const isFormData = data instanceof FormData;
            const response = await this._httpClient.request<T & { meta?: TMetadata }>({
                method: method,
                url: url,
                data: data,
                params: params,
                headers: isFormData ? {} : { 'Content-Type': 'application/json', ...headers },
            });
            result.statusCode = response.status;
            if (response.status >= 200 && response.status <= 300) {
                result.data = response.data;
            } else {
                result = CRMAPIBase.parseErrors<T>(response.status, response.data);
            }
        } catch (error) {
            if (error.response) {
                if (
                    error.response.status === 401 &&
                    error.response.data?.errorCode !== 'WrongCode' &&
                    // Временеый костыль для обхода ошибки 401 у реквизитов, после починки бэка убрать
                    !error.response?.request?.responseURL?.includes('requisites')
                ) {
                    window.location.href = '/login';
                }
                result = CRMAPIBase.parseErrors<T>(error.response.status, error.response.data);
            } else {
                result.statusCode = 500;
                result.errorMessages = [error.message || 'Ошибка сети'];
            }
        }

        this.throwIfError(result);

        return result;
    }

    /**
     * Парсинг ошибок
     * @param data Тело
     * @returns Расшифровка
     */
    public static parseErrors<T>(statusCode: number, data: any): RequestResult<T> {
        const apiError = ErrorHandlingManager.parseApiError(statusCode, data);
        return {
            statusCode: apiError.statusCode,
            errorCode: apiError.errorCode,
            errorMessages: apiError.errorMessages,
            message: apiError.message,
        };
    }

    /**
     * Проверяет, содержит ли результат запроса ошибки, и выбрасывает исключение, если это так
     * @param result Результат запроса для проверки
     * @throws Error если результат запроса содержит ошибки
     */
    protected throwIfError<T>(result: RequestResult<T>): void {
        if (result.errorMessages?.length || result.statusCode >= 400) {
            // Только создаем ошибку и выбрасываем ее, но не обрабатываем на этом уровне
            if (result.statusCode !== 404 && result.statusCode !== 403) {
                ErrorHandlingManager.createAndThrow({
                    statusCode: result.statusCode,
                    errorCode: result.errorCode,
                    errorMessages: result.errorMessages,
                    message: result.message,
                });
            }
        }
    }

    /**
     * Безопасно извлекает данные из ответа API с учетом соответствующей проверки ошибок
     * @param result Результат API-запроса
     * @param dataPath Путь к вложенным данным (например, "data.settings")
     * @returns Извлеченные данные или значение по умолчанию
     * @throws Error если запрос завершился ошибкой
     */
    protected safeExtractData<T>(result: RequestResult<T>, dataPath: string) {
        this.throwIfError(result);

        try {
            const extractedData = dataPath
                .split('.')
                .reduce(
                    (data, key) =>
                        data && typeof data === 'object' && key in data ? data[key] : undefined,
                    result.data as any
                );

            if (!extractedData) {
                throw new Error(`Не удалось извлечь данные по пути ${dataPath}`);
            }

            return extractedData;
        } catch (error) {
            throw new Error(error.message);
        }
    }
}

export { CRMAPIBase };
